/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Formik, type FormikProps } from 'formik'
import { Collapse } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import DateTimePicker from '~/components/DatePicker/DateTimePicker'
import { DatePickerWrapper } from '~/components/DatePicker'
import { Select } from '~/components/Select'
import { activityApiService } from '~/services/api/api.activity'
import { usersApiService } from '~/services/api'
import { useRcmTableData } from '~/hooks/useRcmTableData'
import persistService from '~/services/persist'
import { getDatePickerFormat, getDateTimeFnsParseFormats } from '~/utils/dateFormatting'

interface TableFilterProps {
  fsiId: number
  formRef?: React.RefObject<FormikProps<{ filter: FilterValues }> | null>
}

interface FilterOption {
  value: number | string
  label: string
}

interface FilterValues {
  users: Array<number | string> // Array of values, not option objects
  actions: Array<number | string> // Array of values, not option objects
  from: Date | null
  to: Date | null
  includeSystemGeneratedEvents: boolean
}

const defaultFilter: FilterValues = {
  users: [],
  actions: [],
  from: null,
  to: null,
  includeSystemGeneratedEvents: false
}

const TableFilter: React.FC<TableFilterProps> = ({ fsiId, formRef }) => {
  const { t: translate } = useTranslation()
  const tableData = useRcmTableData()
  const persistanceKey = tableData.persistenceKey + '_filter'
  const [filter, setFilter] = useState<FilterValues | null>(null)
  const [userOptions, setUserOptions] = useState<FilterOption[]>([])
  const [actionOptions, setActionOptions] = useState<FilterOption[]>([])

  // Format date for display in DatePickerWrapper
  const formatDateDisplay = (dateValue: Date | null): string | null => {
    if (!dateValue) return null
    const d = new Date(dateValue)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    const seconds = String(d.getSeconds()).padStart(2, '0')
    return `${year}${month}${day} ${hours}:${minutes}:${seconds}`
  }

  useEffect(() => {
    // Load users
    usersApiService.getAssembledUsersGroups().then((data) => {
      if (data && Array.isArray(data.usersAll)) {
        const options = data.usersAll.map((user: any) => ({
          value: user.id,
          label: user.chName || `${user.firstName} ${user.lastName}`
        }))
        setUserOptions(options)
      }
    })

    // Load actions
    activityApiService.getActions().then((data) => {
      if (data && Array.isArray(data.elements)) {
        const options = data.elements.map((action: any) => ({
          value: action.id,
          label: action.value
        }))
        setActionOptions(options)
      }
    })
  }, [])

  useEffect(() => {
    const persistedFilter = persistService.getBasic(persistanceKey) as FilterValues | null
    const newFilter = persistedFilter ?? defaultFilter

    setFilter(newFilter)

    // Apply initial filter if persisted
    if (persistedFilter) {
      tableData.applyFilter(persistedFilter)
    }
  }, [userOptions, actionOptions])

  const doFilter = (values: FilterValues) => {
    // Filter already contains arrays of values, pass directly to API
    tableData.applyFilter({ ...values })
    persistService.setBasic(persistanceKey, { ...values })
  }

  return (
    <Collapse in={!tableData.isFilterCollapsed}>
      <div className="filter-wrapper">
        {filter && (
          <Formik
            innerRef={formRef}
            initialValues={{ filter }}
            onReset={(values) => {
              persistService.clear(persistanceKey)
              setFilter(defaultFilter)
              values.filter = defaultFilter
              doFilter(defaultFilter)
            }}
            enableReinitialize={true}
            onSubmit={(values) => {
              doFilter(values.filter)
            }}
          >
            {({ values, setFieldValue, handleSubmit, handleReset }) => (
              <form onSubmit={handleSubmit} onReset={handleReset} noValidate>
                <div className="row">
                  <div className="col-xs-4">
                    <div className="form-group">
                      <label style={{ fontSize: '14px' }}>{translate('REPORTS_FILTER_LABEL_USER(S)')}</label>
                      <Select
                        placeholder={translate('USER(S)...')}
                        name="filter.users"
                        options={userOptions}
                        onChange={(selectedOptions: any) => {
                          const values = Array.isArray(selectedOptions) ? selectedOptions.map((opt) => opt.value) : []
                          setFieldValue('filter.users', values)
                        }}
                        value={values.filter.users}
                        multi
                        searchable
                      />
                    </div>
                    <div className="form-group checkbox-form-group" style={{ marginBottom: 20 }}>
                      <input
                        type="checkbox"
                        id="input-obsol"
                        name="filter.includeSystemGeneratedEvents"
                        checked={values.filter.includeSystemGeneratedEvents}
                        onChange={(e) => setFieldValue('filter.includeSystemGeneratedEvents', e.target.checked)}
                      />
                      <label htmlFor="input-obsol">
                        <div className="chkbc"></div>
                        <span>{translate('REPORTS_FILTER_LABEL_INCL_SYSTEM_GENERATED_EVENTS')}</span>
                      </label>
                    </div>
                  </div>
                  <div className="col-xs-4">
                    <div className="form-group">
                      <label style={{ fontSize: '14px' }}>{translate('REPORTS_FILTER_LABEL_ACTION(S)')}</label>
                      <Select
                        placeholder={translate('ACTION(S)...')}
                        name="filter.actions"
                        options={actionOptions}
                        onChange={(selectedOptions: any) => {
                          const values = Array.isArray(selectedOptions) ? selectedOptions.map((opt) => opt.value) : []
                          setFieldValue('filter.actions', values)
                        }}
                        value={values.filter.actions}
                        multi
                        searchable
                      />
                    </div>
                  </div>
                  <div className="col-xs-4">
                    <div className="form-group">
                      <label style={{ fontSize: '14px', display: 'block' }}>
                        {translate('REPORTS_FILTER_LABEL_DATETIME_RANGE')}
                      </label>
                      <div style={{ paddingLeft: 10, paddingRight: 71 }}>
                        <label htmlFor="in-from" style={{ fontSize: 12 }}>
                          {translate('REPORTS_FILTER_LABEL_FROM')}
                        </label>
                        <DatePickerWrapper
                          alwaysEditable={true}
                          value={() => formatDateDisplay(values.filter.from)}
                          input={
                            <DateTimePicker
                              name="filter.from"
                              selected={values.filter.from}
                              onChange={(val: Date | null) => {
                                if (val) {
                                  const dateWithTime = new Date(val)
                                  dateWithTime.setHours(0, 0, 0, 0)
                                  setFieldValue('filter.from', dateWithTime)
                                } else {
                                  setFieldValue('filter.from', val)
                                }
                              }}
                              showTimeInput
                              maxDate={values.filter.to || new Date()}
                              timeInputLabel={translate('TIME_LABEL_CLINICAL_ACTIVITY_REPORT')}
                              className="form-control field-control w100p"
                              id="in-from"
                              placeholderText={translate('PLACEHOLDER_TEXT_DATE_TIME_FORMAT')}
                              showYearDropdown
                            />
                          }
                        />
                        <label htmlFor="in-to" style={{ fontSize: 12, paddingTop: 10 }}>
                          {translate('REPORTS_FILTER_LABEL_TO')}
                        </label>
                        <DatePickerWrapper
                          alwaysEditable={true}
                          value={() => formatDateDisplay(values.filter.to)}
                          input={
                            <DateTimePicker
                              name="filter.to"
                              selected={values.filter.to}
                              onChange={(val: Date | null) => {
                                if (val) {
                                  const dateWithTime = new Date(val)
                                  dateWithTime.setHours(23, 59, 59, 999)
                                  setFieldValue('filter.to', dateWithTime)
                                } else {
                                  setFieldValue('filter.to', val)
                                }
                              }}
                              showTimeInput
                              minDate={values.filter.from || undefined}
                              maxDate={new Date()}
                              timeInputLabel={translate('TIME_LABEL_CLINICAL_ACTIVITY_REPORT')}
                              className="form-control field-control w100p"
                              id="in-to"
                              placeholderText={translate('PLACEHOLDER_TEXT_DATE_TIME_FORMAT')}
                              showYearDropdown
                            />
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="clearfix">
                  <div className="pull-left">
                    <button disabled={tableData.isLoading} className="btn btn-primary" type="submit">
                      {translate('BTN_FILTER')}
                    </button>
                    <button className="btn btn-chita btn-link at-reset" type="reset">
                      {translate('BTN_CLEAR_FILTER')}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        )}
      </div>
    </Collapse>
  )
}

export default TableFilter
